import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PowerBiReportService } from '../../app/powerbi-report/powerbi-report.service';
import { DtoUserProfile, DtoUserReportView } from '../../models/userProfile.model';
import { DtoSubCategory, PackageInformation } from '../../models/package-center.model';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  private localStorageKey = 'sector';

  jwtDecodedToken: any = {};
  tokenTimer: ReturnType<typeof setTimeout> | null = null;
  userProfile: DtoUserProfile | null = null;

  //START: popup window
  private popUpTitleSubject = new BehaviorSubject<string>('');
  popUpTitle: Observable<string> = this.popUpTitleSubject.asObservable();
  setPopUpTitle(title:string){
    this.popUpTitleSubject.next(title)
  }

  private popUpLoadingSubject = new BehaviorSubject<boolean>(true);
  popUpLoading: Observable<boolean> = this.popUpLoadingSubject.asObservable();
  setPopUpLoading(isLoading:boolean){
    this.popUpLoadingSubject.next(isLoading)
  }

  
  // private isPrintPopupWindowSubject = new BehaviorSubject<any>({});
  // isPrintPopupWindow: Observable<any> = this.isPrintPopupWindowSubject.asObservable();
  

  private isPopupWindowClosingSubject = new BehaviorSubject<boolean>(false);
  isPopupWindowClosing: Observable<boolean> = this.isPopupWindowClosingSubject.asObservable();
  //END: popup window

  private isUploadingSubject = new BehaviorSubject<boolean>(true);
  isUploading: Observable<any> = this.isUploadingSubject.asObservable();

  private uploadedFileSubject = new BehaviorSubject<File[]>([]);
  uploadedFile: Observable<File[]> = this.uploadedFileSubject.asObservable();

  private doFileUploadSubject = new Subject<any>();
  doFileUpload: Observable<any> = this.doFileUploadSubject.asObservable();

  private showFilePreviewSubject = new Subject<any>();
  showFilePreview: Observable<any> = this.showFilePreviewSubject.asObservable();

  private fetchNotificationsSubject = new BehaviorSubject<boolean>(false);
  fetchNotifications: Observable<boolean> = this.fetchNotificationsSubject.asObservable();

  private assignedPackageIdSubject = new BehaviorSubject<number>(0);
  assignedPackageId: Observable<number> = this.assignedPackageIdSubject.asObservable();

  private schoolFormValiditySubject = new BehaviorSubject<FormGroup>(this.fb.group({}));
  schoolFormValidity = this.schoolFormValiditySubject.asObservable();

  private roleFormValiditySubject = new BehaviorSubject<FormGroup>(this.fb.group({}));
  roleFormValidity = this.roleFormValiditySubject.asObservable();

  private schoolUserFormValiditySubject = new BehaviorSubject<FormGroup>(this.fb.group({}));
  schoolUserFormValidity = this.schoolUserFormValiditySubject.asObservable();

  private sectorValue = new BehaviorSubject<string | null>(this.getLocalStorageValueOfSector());
  sectorValue$ = this.sectorValue.asObservable();

  private profileUploaded$ = new BehaviorSubject<boolean>(false);
  profileUploaded = this.profileUploaded$.asObservable();

  private loadReport$ = new BehaviorSubject<boolean>(false);
  loadReport = this.loadReport$.asObservable();

  private isEditSubject = new BehaviorSubject<boolean>(false);
  isEdit$ = this.isEditSubject.asObservable();

  private loadReportForSelectedClientAccount$ = new BehaviorSubject<boolean>(false);
  loadReportForSelectedClientAccount = this.loadReportForSelectedClientAccount$.asObservable();

  private loadReportForSelectedIndividualSchool$ = new BehaviorSubject<boolean>(false);
  loadReportForSelectedIndividualSchool = this.loadReportForSelectedIndividualSchool$.asObservable();

  private currentReport$ = new Subject<[packageInformation: PackageInformation, DtoSubCategory | null, string | null]>();
  currentReport = this.currentReport$.asObservable();

  private updatedNotification$ = new BehaviorSubject<{data:boolean, holding:boolean}>(
    {data:true, holding:true}
  );
  updatedNotification = this.updatedNotification$.asObservable();

  public holdingState$ = new BehaviorSubject<{
    underMaintenance: boolean|null;
    holdingDate: Date | null;
  }>({
    underMaintenance: null,
    holdingDate: null
  });

  holdingState = this.holdingState$.asObservable();


  private contrastModeSubject = new BehaviorSubject<number>(0);
  contrastMode$ = this.contrastModeSubject.asObservable();

  private dashboardActionCommandSource = new Subject<[string, number | string | null]>();
  dashboardActionCommand$ = this.dashboardActionCommandSource.asObservable();
  triggerDashboardActionCommand(command: string, args: number | string | null = null) {
    this.dashboardActionCommandSource.next([command, args]);
  }

  private userPreferenceSource = new Subject<[DtoUserProfile, string | null]>();
  userPreference$ = this.userPreferenceSource.asObservable();
  triggerUserPreference(userProfile: DtoUserProfile, hint: string | null = null) {
    this.userPreferenceSource.next([userProfile, hint]);
  }

  private userReportViewSource = new Subject<[DtoUserReportView, string | null]>();
  userReportView$ = this.userReportViewSource.asObservable();
  triggerUserReportViewSource(reportView: DtoUserReportView, hint: string | null = null) {
    this.userReportViewSource.next([reportView, hint]);
  }

  private themeSource = new Subject<[string]>();
  themeSource$ = this.themeSource.asObservable();
  async triggerThemeChange(themeName: string) {
    const promise = firstValueFrom(this.themeSource$)

    this.themeSource.next([themeName]);

    await promise;
  }

  constructor(
    private fb: FormBuilder,
    private jwtHelper: JwtHelperService,
    private msalService: MsalService,
    private router: Router,
    private toasterService: ToastrService,
    private reportService: PowerBiReportService,
    private snackBar: MatSnackBar
  ) {
    this.startTokenTimer();
  }

  getThemePreference() {
    var auth = this.getLoginToken(true)
    if (auth == null) {
      //return localStorage.getItem('theme')??'deesha';
      //always show deesha if not logged in
      return 'deesha';
    }


    var pref = this.getUserProfile();
    var theme = pref?.themeName ?? 'deesha';

    //localStorage.setItem('theme', theme);
    sessionStorage.setItem('theme', theme);
    return theme;
  }

  setThemePreference(themeName: string) {
    var userProfile = this.getUserProfile();
    userProfile.themeName = themeName;
    this.saveUserProfile('theme');
    this.triggerThemeChange(themeName);
  }

  setContrastMode(mode: number) {
    this.contrastModeSubject.next(mode);
    sessionStorage.setItem('contrastMode', mode.toString());

  }

  getUpdatedNotification(data: boolean, holding: boolean) {
    this.updatedNotification$.next({data:data, holding:holding});
  }

  setHoldingState(
    underMaintenance: boolean|null,
    holdingDate: Date | null
  ): void {
    this.holdingState$.next({
      underMaintenance,
      holdingDate
    });
  }

  setReportId(packageInformation: PackageInformation, reportData: DtoSubCategory | null, hint: string | null = null) {
    this.currentReport$.next([packageInformation, reportData, hint]);
  }

  loadReportForSelectedClientAccountCheck(data: boolean) {
    this.loadReportForSelectedClientAccount$.next(data);
  }

  loadReportForSelectedIndividualSchoolCheck(data: boolean) {
    this.loadReportForSelectedIndividualSchool$.next(data);
  }

  setIsEdit(isEdit: boolean) {
    this.isEditSubject.next(isEdit);
  }

  loadReportCheck(data: boolean) {
    this.loadReport$.next(data);
  }

  private getLocalStorageValueOfSector(): string | null {
    return sessionStorage.getItem(this.localStorageKey);
  }

  resetRoleForm(): void {
    let emptyRoleForm = this.fb.group({});
    this.roleFormValiditySubject.next(emptyRoleForm);
  }

  resetSchoolForm(): void {
    const form = this.schoolFormValiditySubject.getValue();
    form?.reset();
    this.updateSchoolFormValidity(form);
  }

  setLocalStorageValueOfSector(value: string): void {
    sessionStorage.setItem(this.localStorageKey, value);
    this.sectorValue.next(value);
  }

  viewDocumentUrl: string = "";


  removeInvalidSchool() {
    const schoolForm = this.schoolFormValiditySubject.getValue();
    const schoolsArray = schoolForm.get('schools') as FormArray;
    for (let i = schoolsArray.length - 1; i >= 0; i--) {
      const schoolGroup = schoolsArray.at(i) as FormGroup;
      if (schoolGroup.invalid) {
        schoolsArray.removeAt(i);
      }
    }
    this.updateSchoolFormValidity(schoolForm); // Ensure the updated form is shared
  }

  updateSchoolFormValidity(form: FormGroup) {
    this.schoolFormValiditySubject.next(form);
  }

  updateRoleFormValidity(form: FormGroup) {
    this.roleFormValiditySubject.next(form);
  }

  updateSchoolUserFormValidity(form: FormGroup) {
    this.schoolUserFormValiditySubject.next(form);
  }

  // printPopupWindow(data:any|null = null){
  //   this.isPrintPopupWindowSubject.next(data)
  // }
  
  closePopupWindow(isClosing:boolean){
    this.isPopupWindowClosingSubject.next(isClosing)
  }
  changeUploadingStatus(data: any) {
    this.isUploadingSubject.next(data);
  }

  profileUpdateCheck(data: boolean) {
    this.profileUploaded$.next(data);
  }

  setUploadedFile(data: File[]) {
    this.uploadedFileSubject.next(data);
  }

  setAssignedPackageId(data: number) {
    this.assignedPackageIdSubject.next(data);
  }

  fetchNotification(data: boolean) {
    this.fetchNotificationsSubject.next(data);
  }

  performFileUpload(data: any) {
    this.doFileUploadSubject.next(data);
  }

  previewFile(data: any) {
    this.showFilePreviewSubject.next(data);
  }

  showDocumentPreview(url: string) {
    var IsImage = true;
    var ext = this.getExtension(url);
    if (ext === '.pdf') {
      var url1 = url;
      var url2 = url1.replaceAll("//", "%2F%2F");
      var url3 = url2.replaceAll("/", "%2F");
      var url4 = url3.replaceAll(":", "%3A");
      var url5 = url4.replaceAll("?", "%3F");
      var url6 = url5.replaceAll("-", "%2D");
      var url7 = url6.replaceAll("&", "%26");
      var url8 = url7.replaceAll("=", "%3D");
      url = url8;
      IsImage = false;
      this.viewDocumentUrl = "https://docs.google.com/viewer?url=" + url + "&embedded=true";
      window.open(this.viewDocumentUrl, "_blank");
    }
    else if (ext === '.doc' || ext === '.docx' || ext === '.ppt' || ext === '.pptx' || ext === '.xls' || ext === '.xlsx') {
      IsImage = false;
      var url1 = url;
      var url2 = url1.replaceAll("//", "%2F%2F");
      var url3 = url2.replaceAll("/", "%2F");
      var url4 = url3.replaceAll(":", "%3A");
      var url5 = url4.replaceAll("?", "%3F");
      var url6 = url5.replaceAll("-", "%2D");
      var url7 = url6.replaceAll("&", "%26");
      var url8 = url7.replaceAll("=", "%3D");
      url = url8;
      this.viewDocumentUrl = "https://view.officeapps.live.com/op/view.aspx?src=" + url;
      window.open(this.viewDocumentUrl, "_blank");
    }
    else {
      window.open(url, "_blank");
    }
  }

  getExtension(url: string) {
    return (url = url.substr(1 + url.lastIndexOf("/")).split('?')[0]).split('#')[0].substr(url.lastIndexOf("."))
  }

  preserveContrastModeAndClearLocalStorage() {
    const contrastMode = sessionStorage.getItem('contrastMode');

    sessionStorage.clear();

    if (contrastMode !== null) {
      sessionStorage.setItem('contrastMode', contrastMode);
    }
  }

  private getTokenExpirationDate(): Date | null {
    try {
      const decoded: any = this.jwtDecodedToken;
      if (!decoded.exp) return null;

      const date = new Date(0);
      date.setUTCSeconds(decoded.exp);
      return date;
    } catch (error) {
      return null;
    }
  }

  public startTokenTimer() {
    const expirationDate = this.getTokenExpirationDate();
    if (!expirationDate) return;

    const timeout = expirationDate.getTime() - Date.now();

    if (timeout > 0) {
      if (this.tokenTimer !== null) {
        clearTimeout(this.tokenTimer);
        this.tokenTimer = null;
      }
      this.tokenTimer = setTimeout(() => {
        this.logout();
        this.toasterService.clear();
        this.toasterService.warning('Session expired. You have been logged out.')
      }, timeout);
    } else {
      this.logout(); // Token already expired
    }
  }

  getLoginToken(allowAnonymous: boolean = false) {
    if (!this.jwtDecodedToken.unique_name) {

      const access_token = sessionStorage.getItem('access_token') as string;
      if (!access_token) {
        if (allowAnonymous) return null;
        this.logout()
        return;
      }

      this.jwtDecodedToken = access_token ? this.jwtHelper.decodeToken(access_token) : {};

    }

    return this.jwtDecodedToken;
  }

  saveUserProfile(hint: string | null = null) {
    const jwtToken = this.getLoginToken();
    this.reportService.SaveUserPreferenceProfile(
      jwtToken.userId,
      this.userProfile!).subscribe(
        () => {
          this.toasterService.success('Your user preferences has been saved.', 'User Preference');
          sessionStorage.setItem('userProfile', JSON.stringify(this.userProfile!));
          this.triggerUserPreference(this.userProfile!, 'startupSubcategory');
        }
      );
  }

  getUserProfile(): DtoUserProfile {
    if (!this.userProfile) {
      var jsonData = sessionStorage.getItem('userProfile') as string;
      this.userProfile = JSON.parse(jsonData) as DtoUserProfile
    }

    return this.userProfile;
  }

  async login(access_token: string) {
    this.preserveContrastModeAndClearLocalStorage();
    sessionStorage.setItem('access_token', access_token);
    this.jwtDecodedToken = access_token ? this.jwtHelper.decodeToken(access_token) : {};
    
    this.setHoldingState(null,null);
    
    if (!this.jwtDecodedToken.userId) {
      this.logout()
      return;
    }

    this.startTokenTimer();

    try {
      this.userProfile = (await firstValueFrom(this.reportService.GetUserPreferenceProfile(this.jwtDecodedToken.userId))) as DtoUserProfile;
      sessionStorage.setItem('userProfile', JSON.stringify(this.userProfile));
    }
    catch (e) {
      this.toasterService.warning('Unable to get User Profile');
      this.triggerThemeChange(this.userProfile?.themeName ?? 'deesha');
    }
  }

  async logout() {
    this.setHoldingState(null,null);
    sessionStorage.removeItem('access_token');
    if (this.tokenTimer) clearTimeout(this.tokenTimer);
    this.tokenTimer = null;

    this.preserveContrastModeAndClearLocalStorage();
    this.snackBar.dismiss();
    await this.triggerThemeChange('')
    let accounts = this.msalService.instance.getAllAccounts().length;

    if (accounts > 0) {
      this.msalService.logoutRedirect();
    }

    this.router.navigate(['/login']);
  }

}
